import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { AppSidebar } from '@/components/app-sidebar';
import { useLocation } from 'react-router';
import { cn } from '@/lib/utils';
import { AnnouncementBar } from '@/components/announcement-bar';
import { AppUnderConstructionBar } from '@/components/app-under-construction-bar';
import { useChatwootWidget } from '@/components/chatwoot-widget/chatwoot-widget-context';
import {
  MobilePageHeader,
  type MobilePageHeaderProps,
} from '@/components/shell/mobile-page-header';
import { MobileTabBar } from '@/components/shell/mobile-tab-bar';
import { MoreSheet } from '@/components/shell/more-sheet';
import { NewCampaignFab } from '@/components/shell/new-campaign-fab';
import { StoreSwitcherSheet } from '@/components/shell/store-switcher-sheet';
import { useActiveSubscription } from '@/hooks/use-billing';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTenant } from '@/hooks/use-tenant';
import { isMoreSectionActive } from '@/lib/navigation';

export interface SidebarLayoutProps {
  children: React.ReactNode;
  className?: string;
  sideRail?: React.ReactNode;
  /** Replaces the default mobile header (Phases 2–4: large title, actions). */
  mobileHeader?: Omit<
    MobilePageHeaderProps,
    'onOpenStoreSwitcher' | 'storeTriggerRef'
  >;
  /** Shows the "Nova campanha" FAB on mobile (Início and Campanhas). */
  showNewCampaignFab?: boolean;
}

export function SidebarLayout({
  children,
  className,
  sideRail,
  mobileHeader,
  showNewCampaignFab = false,
}: SidebarLayoutProps) {
  const pathname = useLocation().pathname;
  const isLoginPage = pathname === '/login';
  const isMobile = useIsMobile();

  if (isLoginPage) {
    return (
      <div className="flex h-screen">
        <main
          className={cn(
            'flex-1 p-6 md:p-8 h-full overflow-y-auto relative flex flex-col max-h-full w-full',
            className
          )}
        >
          {children}
        </main>
      </div>
    );
  }

  const mainClassName = cn(
    'relative flex min-h-0 min-w-0 max-h-full flex-1 flex-col p-6 md:p-8',
    sideRail ? 'h-full overflow-hidden' : 'h-full overflow-y-auto',
    className
  );

  if (isMobile) {
    return (
      <MobileShell
        pathname={pathname}
        mainClassName={mainClassName}
        mobileHeader={mobileHeader}
        showNewCampaignFab={showNewCampaignFab}
      >
        {children}
      </MobileShell>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col">
      <AnnouncementBar />
      <AppUnderConstructionBar />
      <div className="flex min-h-0 flex-1">
        <AppSidebar />
        <main className={mainClassName}>{children}</main>
        {sideRail && (
          <aside className="hidden h-full w-[26rem] min-h-0 shrink-0 flex-col overflow-hidden border-l bg-background lg:flex">
            {sideRail}
          </aside>
        )}
      </div>
    </div>
  );
}

/**
 * Chrome below 768px (SHELL-01/02/04): warning bars, header with the active
 * store, the page, the optional FAB, the tab bar in the flow of the column,
 * and the Mais / store switcher sheets.
 */
function MobileShell({
  children,
  pathname,
  mainClassName,
  mobileHeader,
  showNewCampaignFab,
}: {
  children: React.ReactNode;
  pathname: string;
  mainClassName: string;
  mobileHeader: SidebarLayoutProps['mobileHeader'];
  showNewCampaignFab: boolean;
}) {
  const tenant = useTenant();
  const { data: subscription } = useActiveSubscription();
  const { data: featureFlags } = useFeatureFlags();
  const { setHasShellOnScreen } = useChatwootWidget();
  const [moreOpen, setMoreOpen] = useState(false);
  const [storeSwitcherOpen, setStoreSwitcherOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const storeTriggerRef = useRef<HTMLButtonElement>(null);

  // The Help FAB must know there is a shell on screen on mobile too.
  useEffect(() => {
    setHasShellOnScreen(true);
    return () => setHasShellOnScreen(false);
  }, [setHasShellOnScreen]);

  const moreActive = isMoreSectionActive(
    {
      tenantSlug: tenant.slug,
      audiencesEnabled: featureFlags?.audiencesEnabled ?? false,
      hasActiveSubscription: !!subscription,
      fromShopifyAppStore: tenant.fromShopifyAppStore,
    },
    pathname
  );

  return (
    <div className="flex h-dvh w-full flex-col pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <AnnouncementBar />
      <AppUnderConstructionBar />
      <MobilePageHeader
        {...mobileHeader}
        onOpenStoreSwitcher={() => setStoreSwitcherOpen(true)}
        storeTriggerRef={storeTriggerRef}
      />
      <div className="relative flex min-h-0 flex-1">
        <main className={mainClassName}>{children}</main>
        {showNewCampaignFab && <NewCampaignFab tenantSlug={tenant.slug} />}
      </div>
      <MobileTabBar
        tenantSlug={tenant.slug}
        pathname={pathname}
        moreActive={moreActive}
        onMoreClick={() => setMoreOpen(true)}
        moreButtonRef={moreButtonRef}
      />
      <MoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        onSwitchStore={() => {
          setMoreOpen(false);
          setStoreSwitcherOpen(true);
        }}
        returnFocusRef={moreButtonRef}
      />
      <StoreSwitcherSheet
        open={storeSwitcherOpen}
        onOpenChange={setStoreSwitcherOpen}
        returnFocusRef={storeTriggerRef}
      />
    </div>
  );
}
